import { observable } from '@legendapp/state';
import { syncObservable } from '@legendapp/state/sync';
import { ObservablePersistLocalStorage } from '@legendapp/state/persist-plugins/local-storage';
import { trackEvent } from '../lib/analytics';

// UI state - persisted locally
export const ui$ = observable({
  activeNoteId: null,
  viewMode: 'notes',
  theme: 'system',
  selectedTagFilter: null,
  snippetFilter: null,
  selectedNoteIds: [],
  selectedSnippetIds: [],
  showMigrationModal: false,
  migrationProgress: 0,
  showArchived: true, // Show archived notes by default
  isMobileSidebarOpen: false, // Mobile drawer state
  searchTerm: '', // Search filter for notes
  selectedSharedTag: null, // { id, tagPath, ownerName, ownerId } or null
  viewingSharedNote: null, // { noteId, authorName, note } when viewing another's note read-only
  onboardingCompleted: false, // Whether user has completed the welcome tour
  smartTagsEnabled: true, // Show AI smart tags in sidebar, editor, and note cards
});

// Persist UI state to localStorage
syncObservable(ui$, {
  persist: {
    name: 'promptnote-ui',
    plugin: ObservablePersistLocalStorage,
  },
});

// Helper functions
export const setActiveNoteId = (id) => {
  ui$.activeNoteId.set(id);
};

export const setViewMode = (mode) => {
  ui$.viewMode.set(mode);
};

export const setTheme = (theme) => {
  // console.log('[UI] setTheme called with:', theme);
  ui$.theme.set(theme);
  trackEvent('theme_changed', { theme });
  // console.log('[UI] After set, ui$.theme.get():', ui$.theme.get());
};

export const setSelectedTagFilter = (tag) => {
  ui$.selectedTagFilter.set(tag);
  // Mutual exclusion: clear shared tag when selecting personal tag
  if (tag !== null) {
    ui$.selectedSharedTag.set(null);
  }
};

export const setSelectedSharedTag = (sharedTag) => {
  ui$.selectedSharedTag.set(sharedTag);
  // Mutual exclusion: clear personal tag filter when selecting shared tag
  if (sharedTag !== null) {
    ui$.selectedTagFilter.set(null);
    ui$.activeNoteId.set(null);
  }
};

export const setSnippetFilter = (filter) => {
  ui$.snippetFilter.set(filter);
};

export const toggleNoteSelection = (noteId) => {
  const selected = ui$.selectedNoteIds.get();
  if (selected.includes(noteId)) {
    ui$.selectedNoteIds.set(selected.filter(id => id !== noteId));
  } else {
    ui$.selectedNoteIds.set([...selected, noteId]);
  }
};

export const toggleSnippetSelection = (snippetId) => {
  const selected = ui$.selectedSnippetIds.get();
  if (selected.includes(snippetId)) {
    ui$.selectedSnippetIds.set(selected.filter(id => id !== snippetId));
  } else {
    ui$.selectedSnippetIds.set([...selected, snippetId]);
  }
};

export const clearSelection = () => {
  ui$.selectedNoteIds.set([]);
  ui$.selectedSnippetIds.set([]);
};

export const toggleShowArchived = () => {
  ui$.showArchived.set(!ui$.showArchived.get());
};

export const setMobileSidebarOpen = (isOpen) => {
  ui$.isMobileSidebarOpen.set(isOpen);
};

export const toggleMobileSidebar = () => {
  ui$.isMobileSidebarOpen.set(!ui$.isMobileSidebarOpen.get());
};

// Centralized mobile navigation: clears active note and closes sidebar drawer.
// Call this whenever a sidebar item is clicked to ensure mobile returns to list view.
export const navigateOnMobile = () => {
  ui$.activeNoteId.set(null);
  ui$.isMobileSidebarOpen.set(false);
};

export const setSearchTerm = (term) => {
  ui$.searchTerm.set(term);
};

